import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, errText } from "../lib/api";
import { CITY_COORDS, PROV_CENTER } from "../lib/constants";
import { useLanguage } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { LostFoundId } from "./LostFoundId";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Loader2, MapPin } from "lucide-react";

const PIN = L.divIcon({
  className: "",
  html: `<svg width="30" height="40" viewBox="0 0 30 40"><path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="#DC2626"/><circle cx="15" cy="15" r="6" fill="#fff"/></svg>`,
  iconSize: [30, 40], iconAnchor: [15, 40],
});
const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

function MapHelpers({ center, onPick }) {
  const map = useMap();
  useEffect(() => { const id = setTimeout(() => { map.invalidateSize(); map.setView(center, 13); }, 200); return () => clearTimeout(id); }, [map, center]);
  useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
  return null;
}

export function QuickSightingDialog({ listing, open, onOpenChange }) {
  const { t } = useLanguage();
  const fallback = CITY_COORDS[(listing.city || "").trim().toLowerCase()] || PROV_CENTER[listing.province] || PROV_CENTER.all;
  const [center, setCenter] = useState(fallback);
  const [pin, setPin] = useState(null);
  const [form, setForm] = useState({ seen_at: nowLocal(), note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin(null); setForm({ seen_at: nowLocal(), note: "" });
    api.get(`/geo/city?city=${encodeURIComponent(listing.city || "")}&province=${listing.province || ""}`)
      .then(({ data }) => data.lat != null && setCenter([data.lat, data.lng])).catch(() => {});
  }, [open, listing.city, listing.province]);

  const submit = async () => {
    if (!pin) return toast.error(t("sg_need_pin"));
    setBusy(true);
    try {
      await api.post(`/listings/${listing.id}/sightings`, { lat: pin[0], lng: pin[1], seen_at: new Date(form.seen_at).toISOString(), note: form.note });
      toast.success(t("sg_sent"));
      onOpenChange(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid={`quick-sighting-dialog-${listing.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap"><MapPin size={18} className="text-red-600" /> {t("qs_title").replace("{name}", listing.lf_name || listing.title)} <LostFoundId listing={listing} testid="quick-sighting-lf-id" /></DialogTitle>
          <DialogDescription>{t("qs_hint")}</DialogDescription>
        </DialogHeader>
        <div className={`relative z-0 h-64 rounded-xl overflow-hidden border-2 ${pin ? "border-red-400" : "border-slate-200"} cursor-crosshair`} data-testid="quick-sighting-map">
          {open && (
            <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
              <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapHelpers center={center} onPick={setPin} />
              {pin && <Marker position={pin} icon={PIN} />}
            </MapContainer>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("sg_when")}</Label>
            <Input type="datetime-local" value={form.seen_at} max={nowLocal()} onChange={(e) => setForm((f) => ({ ...f, seen_at: e.target.value }))} data-testid="quick-sighting-when" className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">{t("sg_note")}</Label>
            <Textarea rows={1} value={form.note} maxLength={500} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder={t("sg_note_ph")} data-testid="quick-sighting-note" className="mt-1 min-h-10 rounded-xl" />
          </div>
        </div>
        <Button onClick={submit} disabled={busy || !pin} data-testid="quick-sighting-submit" className="w-full rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} {t("sg_submit")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
